/**
 * Secure API Error class with enhanced error handling and information sanitization
 * Prevents sensitive information leakage while providing useful debugging info
 */
export class APIError extends Error {
  constructor(message, statusCode = 500, url = '', details = {}) {
    // Sanitize the message to prevent information leakage
    const sanitizedMessage = APIError.sanitizeErrorMessage(message, statusCode);
    super(sanitizedMessage);

    this.name = 'APIError';
    this.statusCode = statusCode;
    this.url = APIError.sanitizeUrl(url);
    this.details = APIError.sanitizeDetails(details);
    this.timestamp = new Date().toISOString();
    this.requestId = this.generateRequestId();
    this.securityLevel = 'public-safe';

    // Categorize error type for better handling
    this.errorType = this.categorizeError(statusCode);

    // Ensure the error is properly configured
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIError);
    }
  }

  /**
   * Sanitize error message to prevent information disclosure
   */
  static sanitizeErrorMessage(message, statusCode) {
    if (!message || typeof message !== 'string') {
      return 'An error occurred';
    }

    // Production error messages should be generic for security
    if (process.env.NODE_ENV === 'production') {
      const productionMessages = {
        400: 'Invalid request format',
        401: 'Authentication required',
        403: 'Access forbidden',
        404: 'Resource not found',
        408: 'Request timeout',
        429: 'Too many requests. Please slow down.',
        500: 'Internal server error',
        502: 'Service temporarily unavailable',
        503: 'Service temporarily unavailable',
        504: 'Gateway timeout'
      };

      return productionMessages[statusCode] || 'An error occurred';
    }

    // In development, sanitize but allow more detail
    let sanitized = message
      .replace(/[\r\n\t]/g, ' ') // Remove newlines and tabs
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    // Remove potentially sensitive patterns
    const sensitivePatterns = [
      /password[^a-zA-Z0-9]/gi,
      /token[^a-zA-Z0-9]/gi,
      /secret[^a-zA-Z0-9]/gi,
      /key[^a-zA-Z0-9]/gi,
      /authorization/gi,
      /bearer\s+[a-zA-Z0-9_-]+/gi,
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi, // Email addresses
      /\b(?:\d{1,3}\.){3}\d{1,3}\b/g, // IP addresses
      /\b[a-fA-F0-9]{32,}\b/g // Potential hashes/tokens
    ];

    sensitivePatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    });

    // Truncate long messages
    if (sanitized.length > 200) {
      sanitized = sanitized.substring(0, 200) + '...';
    }

    return sanitized || 'An error occurred';
  }

  /**
   * Sanitize URL to prevent information leakage
   */
  static sanitizeUrl(url) {
    if (!url || typeof url !== 'string') return '';

    try {
      const parsedUrl = new URL(url);

      // Remove sensitive query parameters
      const sensitiveParams = ['token', 'key', 'secret', 'password', 'auth'];
      sensitiveParams.forEach(param => {
        if (parsedUrl.searchParams.has(param)) {
          parsedUrl.searchParams.set(param, '[REDACTED]');
        }
      });

      // In production, only show the pathname
      if (process.env.NODE_ENV === 'production') {
        return parsedUrl.pathname;
      }

      return parsedUrl.toString();
    } catch {
      // If URL parsing fails, return sanitized version
      return url.split('?')[0] || url;
    }
  }

  /**
   * Sanitize error details object
   */
  static sanitizeDetails(details) {
    if (!details || typeof details !== 'object') return {};

    const sanitized = {};
    const allowedKeys = [
      'statusText', 'timestamp', 'requestId', 'errorType',
      'method', 'endpoint', 'attempt', 'fingerprint'
    ];

    // Only include safe keys
    Object.keys(details).forEach(key => {
      if (allowedKeys.includes(key)) {
        sanitized[key] = details[key];
      }
    });

    return sanitized;
  }

  /**
   * Generate unique request ID for error tracking
   */
  generateRequestId() {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `err_${timestamp}_${random}`;
  }

  /**
   * Categorize error type based on status code
   */
  categorizeError(statusCode) {
    if (statusCode >= 400 && statusCode < 500) {
      if (statusCode === 401) return 'authentication';
      if (statusCode === 403) return 'authorization';
      if (statusCode === 404) return 'not_found';
      if (statusCode === 429) return 'rate_limit';
      return 'client_error';
    }

    if (statusCode >= 500) {
      if (statusCode === 502 || statusCode === 503 || statusCode === 504) {
        return 'service_unavailable';
      }
      return 'server_error';
    }

    if (statusCode === 0) return 'network_error';

    return 'unknown';
  }

  /**
   * Check if this is a network error
   */
  isNetworkError() {
    return this.errorType === 'network_error' || this.statusCode === 0;
  }

  /**
   * Check if this is a client error (4xx)
   */
  isClientError() {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  /**
   * Check if this is a server error (5xx)
   */
  isServerError() {
    return this.statusCode >= 500;
  }

  /**
   * Check if this is a rate limit error
   */
  isRateLimitError() {
    return this.errorType === 'rate_limit' || this.statusCode === 429;
  }

  /**
   * Check if this error is retryable
   */
  isRetryable() {
    // Don't retry client errors (except 408, 429) or authentication errors
    if (this.isClientError() && ![408, 429].includes(this.statusCode)) {
      return false;
    }

    // Retry network errors and some server errors
    return this.isNetworkError() ||
           this.isServerError() ||
           [408, 502, 503, 504].includes(this.statusCode);
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage() {
    const userMessages = {
      'network_error': 'Unable to connect. Please check your internet connection.',
      'authentication': 'Please log in to continue.',
      'authorization': 'You don\'t have permission to access this resource.',
      'not_found': 'The requested resource was not found.',
      'rate_limit': 'Too many requests. Please wait a moment and try again.',
      'service_unavailable': 'Service is temporarily unavailable. Please try again later.',
      'server_error': 'Something went wrong on our end. Please try again later.',
      'client_error': 'There was a problem with your request. Please check and try again.'
    };

    return userMessages[this.errorType] || 'An unexpected error occurred. Please try again.';
  }

  /**
   * Get error details for logging (sanitized)
   */
  getLoggingDetails() {
    return {
      requestId: this.requestId,
      errorType: this.errorType,
      statusCode: this.statusCode,
      url: this.url,
      timestamp: this.timestamp,
      message: this.message,
      details: this.details,
      securityLevel: this.securityLevel
    };
  }

  /**
   * Convert to JSON representation
   */
  toJSON() {
    return {
      name: this.name,
      message: this.getUserMessage(), // Use user-friendly message
      statusCode: this.statusCode,
      errorType: this.errorType,
      timestamp: this.timestamp,
      requestId: this.requestId,
      retryable: this.isRetryable()
    };
  }

  /**
   * Convert to string representation
   */
  toString() {
    return `${this.name} [${this.statusCode}]: ${this.getUserMessage()} (${this.requestId})`;
  }

  /**
   * Create error from response
   */
  static fromResponse(response, url = '', additionalDetails = {}) {
    const statusCode = response.status;
    const statusText = response.statusText;

    const details = {
      statusText,
      timestamp: new Date().toISOString(),
      ...additionalDetails
    };

    return new APIError(
      `HTTP ${statusCode}: ${statusText}`,
      statusCode,
      url,
      details
    );
  }

  /**
   * Create network error
   */
  static createNetworkError(originalError) {
    const details = {
      originalError: originalError.name,
      timestamp: new Date().toISOString()
    };

    return new APIError(
      'Network request failed',
      0,
      '',
      details
    );
  }

  /**
   * Create timeout error
   */
  static createTimeoutError(url = '') {
    const details = {
      timestamp: new Date().toISOString(),
      errorType: 'timeout'
    };

    return new APIError(
      'Request timeout',
      408,
      url,
      details
    );
  }

  /**
   * Create rate limit error
   */
  static createRateLimitError(endpoint = '') {
    const details = {
      timestamp: new Date().toISOString(),
      endpoint
    };

    return new APIError(
      'Rate limit exceeded',
      429,
      endpoint,
      details
    );
  }
}